import type { ThematicLegendData } from './api'

/** What an element is drawn with: a class colour, or one of the two residual buckets. */
export interface ElementColour {
  colour: string
  /** The class value, or `__unset` / `__not_applicable`. */
  classValue: string
  /** A lot whose spaces are painted with it: its own fill is left to them. */
  holdsMembers?: boolean
}

export const UNSET = '__unset'
export const NOT_APPLICABLE = '__not_applicable'
export const UNMATCHED = '__unmatched'

// Design tokens for the two residual buckets, so they follow light and dark.
export const UNSET_COLOUR = 'var(--muted-foreground)'
export const NOT_APPLICABLE_COLOUR = 'var(--border)'

/** Picks the label of a multilingual object for the current language. */
export function pickLabel(label: Record<string, string> | undefined, lang: string, fallback = ''): string {
  if (!label) return fallback
  const short = lang.slice(0, 2)
  return label[short] || label.en || Object.values(label)[0] || fallback
}

/** Element uid -> how a legend colours it. Empty when there is no legend. */
export function buildColourIndex(legend: ThematicLegendData | undefined | null): Map<string, ElementColour> {
  const index = new Map<string, ElementColour>()
  if (!legend) return index
  const holders = new Set(Object.values(legend.member_of ?? {}))
  // An element and the spaces drawn inside it share one colour and one class.
  const put = (uids: string[] | undefined, members: string[] | undefined, colour: string, classValue: string) => {
    for (const uid of uids ?? []) {
      index.set(uid, { colour, classValue, ...(holders.has(uid) ? { holdsMembers: true } : {}) })
    }
    for (const uid of members ?? []) index.set(uid, { colour, classValue })
  }
  for (const c of legend.classes ?? []) {
    put(c.asset_uids, c.member_uids, c.colour ?? UNSET_COLOUR, c.value ?? '')
  }
  put(legend.unset?.asset_uids, legend.unset?.member_uids, UNSET_COLOUR, UNSET)
  put(legend.not_applicable?.asset_uids, legend.not_applicable?.member_uids, NOT_APPLICABLE_COLOUR, NOT_APPLICABLE)
  // A value no class of a definition takes: drawn like "not applicable", counted apart.
  put(legend.unmatched?.asset_uids, legend.unmatched?.member_uids, NOT_APPLICABLE_COLOUR, UNMATCHED)
  return index
}

/** True when an isolated class is set and the element is not in it. */
export function isDimmed(
  index: Map<string, ElementColour>,
  uid: string,
  isolated: string | null,
): boolean {
  if (!isolated) return false
  return index.get(uid)?.classValue !== isolated
}

const SHAPES = ['path', 'polygon', 'rect', 'circle', 'ellipse']

/**
 * CSS that paints a plan's drawn elements by thematic. The plan engine stamps
 * `data-asset-uid` on every space and piece of equipment it draws, so one rule
 * per element is enough and the engine stays unaware of thematics. An isolated
 * class dims the elements outside it.
 */
export function plotStyleSheet(index: Map<string, ElementColour>, isolated: string | null): string {
  const rules: string[] = []
  index.forEach((hit, uid) => {
    if (!/^[0-9a-fA-F-]{8,40}$/.test(uid)) return
    const dim = isolated !== null && hit.classValue !== isolated
    // The halo of a lot is an invisible hit target: it must stay unfilled.
    const sel = SHAPES.flatMap((tag) => [
      `${tag}[data-asset-uid="${uid}"]:not(.plan-grouper-halo)`,
      `[data-asset-uid="${uid}"] ${tag}:not(.plan-grouper-halo)`,
    ]).join(',')
    // A lot whose spaces are painted leaves the fill to them, so a room inside
    // reads like a bare lot of the same class, not a darker double layer.
    const opacity = hit.holdsMembers ? 0 : dim ? 0.1 : 0.6
    rules.push(`${sel}{fill:${hit.colour} !important;fill-opacity:${opacity} !important}`)
  })
  return rules.join('\n')
}
